import { defineStore } from 'pinia'
import { store } from '../index'
import { AuthLoginRespVO, LoginInfo, UserInfo } from '@/api/clinet/auth/login'
import { ElMessageBox } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { loginOutApi } from '@/api/clinet/auth/login'
import { useTagsViewStore } from './tagsView'
import router from '@/router'
import { getInitialRedirectPath } from '@/utils/routerHelper'

interface UserState {
  userInfo?: UserInfo
  tokenKey: string
  token: string
  roleRouters?: string[] | AppCustomRouteRecordRaw[]
  rememberMe: boolean
  loginInfo?: LoginInfo
  accountInfo?: AuthLoginRespVO
  tenantKey: string
  currentTenantCode: string
  hasTenantAccess: boolean
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      tokenKey: 'Authorization',
      token: '',
      tenantKey: 'tenant-code',
      currentTenantCode: 'DEFAULT',
      hasTenantAccess: false,
      roleRouters: undefined,
      // 记住我
      rememberMe: true,
      loginInfo: undefined,
      accountInfo: undefined
    }
  },
  getters: {
    getTokenKey(): string {
      return this.tokenKey
    },
    getToken(): string {
      return this.token
    },
    getTenantKey(): string {
      return this.tenantKey
    },
    getCurrentTenantCode(): string {
      return this.currentTenantCode
    },
    getHasTenantAccess(): boolean {
      return this.hasTenantAccess
    },
    getUserInfo(): UserInfo | undefined {
      return this.userInfo
    },
    getRoleRouters(): string[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters
    },
    getRememberMe(): boolean {
      return this.rememberMe
    },
    getLoginInfo(): LoginInfo | undefined {
      return this.loginInfo
    },
    getAccountInfo(): AuthLoginRespVO | undefined {
      return this.accountInfo
    }
  },
  actions: {
    setTokenKey(tokenKey: string) {
      this.tokenKey = tokenKey
    },
    setToken(token: string) {
      this.token = token
    },
    setTenantKey(tenantKey: string) {
      this.tenantKey = tenantKey
    },
    setCurrentTenantCode(currentTenantCode: string) {
      this.currentTenantCode = currentTenantCode
    },
    setHasTenantAccess(hasTenantAccess: boolean) {
      this.hasTenantAccess = hasTenantAccess
    },
    setAccountInfo(authLoginRespVo?: AuthLoginRespVO) {
      this.accountInfo = authLoginRespVo
      this.token = authLoginRespVo?.token ?? ''
    },
    setUserInfo(userInfo?: UserInfo) {
      this.userInfo = userInfo
    },
    setRoleRouters(roleRouters: string[] | AppCustomRouteRecordRaw[]) {
      this.roleRouters = roleRouters
    },
    logoutConfirm() {
      const { t } = useI18n()
      ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
        confirmButtonText: t('common.ok'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      })
        .then(async () => {
          const res = await loginOutApi().catch(() => {})
          if (res) {
            this.reset()
          }
        })
        .catch(() => {})
    },
    reset() {
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()
      this.setToken('')
      this.setUserInfo(undefined)
      this.setAccountInfo(undefined)
      this.setRoleRouters([])
      this.setCurrentTenantCode('DEFAULT')
      this.setHasTenantAccess(false)

      // 退出逻辑中
      const redirectPath = getInitialRedirectPath(router.currentRoute.value)
      console.log('退出：', redirectPath)
      router.replace(redirectPath)
    },
    logout() {
      this.reset()
    },
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
    },
    setLoginInfo(loginInfo: LoginInfo | undefined) {
      this.loginInfo = loginInfo
    }
  },
  persist: true
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
